import { parseDate } from '@internationalized/date';
import { DateTime } from 'luxon';

// e.g. "Thu, 15/05"
export const formatDate = (isoDate: string, locale: string): string =>
  DateTime.fromISO(isoDate).setLocale(locale).toLocaleString({ weekday: 'short', day: 'numeric', month: '2-digit' });

// e.g. "Thu, 15/05/26"
export const formatDateLong = (isoDate: string, locale: string): string =>
  DateTime.fromISO(isoDate)
    .setLocale(locale)
    .toLocaleString({ weekday: 'short', day: 'numeric', month: '2-digit', year: '2-digit' });

// e.g. "15/05"
export const formatDateShort = (isoDate: string, locale: string): string =>
  DateTime.fromISO(isoDate).setLocale(locale).toLocaleString({ day: '2-digit', month: '2-digit' });

// The pattern comes from the locale: the connector and the hour separator are language-specific
// ("à 14h55", "at 14:55"). e.g. "28 déc. 2023 à 14h55"
export const formatDateTime = (isoDateTime: string, locale: string, pattern: string): string =>
  DateTime.fromISO(isoDateTime).setLocale(locale).toFormat(pattern);

export const formatDateRange = (
  departure: string | undefined,
  returnDate: string | undefined,
  locale: string,
  dateTo: string,
): string => {
  if (!departure) return '';
  const formatted = formatDate(departure, locale);
  if (!returnDate) return formatted;
  return `${formatted} ${dateTo} ${formatDate(returnDate, locale)}`;
};

const MAX_SEARCH_DEPARTURE_OFFSET_MONTHS = 12;

export const toDateValue = (isoDate: string) => {
  try {
    return parseDate(isoDate);
  } catch {
    return;
  }
};

// Without a minimum from the white label, today is the earliest departure.
export const getMinSearchDepartureDateIso = (minDaysBeforeDeparture?: number): string =>
  DateTime.local().startOf('day').plus({ days: minDaysBeforeDeparture }).toISODate() ?? '';

export const getMaxSearchDepartureDateIso = (): string =>
  DateTime.local().startOf('day').plus({ months: MAX_SEARCH_DEPARTURE_OFFSET_MONTHS }).toISODate() ?? '';

export const isSearchDepartureDateAllowed = (date?: string, minDaysBeforeDeparture?: number): boolean => {
  if (!date) return false;
  const dateValue = DateTime.fromISO(date).startOf('day');
  const minDepartureDate = DateTime.fromISO(getMinSearchDepartureDateIso(minDaysBeforeDeparture)).startOf('day');
  return dateValue.isValid && minDepartureDate.isValid && dateValue >= minDepartureDate;
};

export const isSearchDepartureDateNotTooFar = (date?: string): boolean => {
  if (!date) return false;
  const dateValue = DateTime.fromISO(date).startOf('day');
  const maxDepartureDate = DateTime.fromISO(getMaxSearchDepartureDateIso()).startOf('day');
  return dateValue.isValid && maxDepartureDate.isValid && dateValue <= maxDepartureDate;
};
